/**
 * Sanitize values used in React `<a href>` / MUI `Button component="a" href={...}`.
 * Allows only http(s) URLs with no embedded credentials — blocks javascript:, data:, etc.
 */

export function sanitizeHttpUrlForHref(raw: string | null | undefined): string | null {
  if (raw == null || typeof raw !== 'string') return null;
  let s = raw.trim();
  if (!s) return null;

  const lowerStart = s.slice(0, 16).toLowerCase();
  if (
    lowerStart.startsWith('javascript:') ||
    lowerStart.startsWith('data:') ||
    lowerStart.startsWith('vbscript:') ||
    lowerStart.startsWith('file:')
  ) {
    return null;
  }

  if (!/^https?:\/\//i.test(s)) {
    s = `https://${s}`;
  }

  let u: URL;
  try {
    u = new URL(s);
  } catch {
    return null;
  }

  if (u.protocol !== 'http:' && u.protocol !== 'https:') {
    return null;
  }
  if (!u.hostname || u.username || u.password) {
    return null;
  }

  return u.toString();
}

/**
 * Safe value for `<img src>` / `CardMedia image={…}`: allows http(s) after URL parsing,
 * or same-site paths starting with a single `/` (blocks `//` protocol-relative).
 */
export function sanitizeImgSrc(raw: string | null | undefined, fallback: string): string {
  if (raw == null || typeof raw !== 'string') return fallback;
  const s = raw.trim();
  if (!s) return fallback;

  if (typeof window !== 'undefined' && s.startsWith('blob:')) {
    const prefix = `blob:${window.location.origin}/`;
    if (s.startsWith(prefix)) {
      return s;
    }
    return fallback;
  }

  const lowerStart = s.slice(0, 16).toLowerCase();
  if (
    lowerStart.startsWith('javascript:') ||
    lowerStart.startsWith('vbscript:') ||
    lowerStart.startsWith('data:')
  ) {
    return fallback;
  }

  if (s.startsWith('/')) {
    if (s.startsWith('//')) return fallback;
    return s;
  }

  return sanitizeHttpUrlForHref(s) ?? fallback;
}

/**
 * Build a safe `tel:` href — strips anything that is not a typical phone character.
 */
export function sanitizeTelHref(raw: string | null | undefined): string | null {
  if (raw == null || typeof raw !== 'string') return null;
  const inner = raw.replace(/^tel:/i, '').trim();
  if (!inner) return null;
  const cleaned = inner.replace(/[^\d+()\-.\s]/g, '').replace(/\s+/g, ' ').trim();
  if (cleaned.replace(/\D/g, '').length < 3) {
    return null;
  }
  return `tel:${cleaned}`;
}

/**
 * Safe `mailto:` for `<a href>` — rejects malicious payloads in the address part.
 */
export function sanitizeMailtoHref(raw: string | null | undefined): string | null {
  if (raw == null || typeof raw !== 'string') return null;
  const addr = raw.replace(/^mailto:/i, '').split('?')[0].trim();
  if (!addr || addr.length > 254) return null;
  if (/[\s<>"]/.test(addr)) return null;
  if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(addr)) return null;
  return `mailto:${addr}`;
}

/**
 * Allow only PNG/JPEG data URLs from our QR generator (`qrcode` library) for img/src and downloads.
 */
export function sanitizeDataUrlForQrImage(raw: string | null | undefined): string | null {
  if (raw == null || typeof raw !== 'string') return null;
  const s = raw.trim().replace(/\s+/g, '');
  if (s.length < 30 || s.length > 6_000_000) return null;
  const png = 'data:image/png;base64,';
  const jpeg = 'data:image/jpeg;base64,';
  let payload: string;
  if (s.startsWith(png)) {
    payload = s.slice(png.length);
  } else if (s.startsWith(jpeg)) {
    payload = s.slice(jpeg.length);
  } else {
    return null;
  }
  if (!/^[a-zA-Z0-9+/=]+$/.test(payload)) return null;
  return s.startsWith(png) ? png + payload : jpeg + payload;
}

/** Stripe-hosted redirects returned by our APIs (Checkout session, Customer Portal, Connect). */
export function isStripeHostedRedirectUrl(raw: string | null | undefined): boolean {
  const s = sanitizeHttpUrlForHref(raw ?? '');
  if (!s) return false;
  try {
    const host = new URL(s).hostname.toLowerCase();
    return (
      host === 'checkout.stripe.com' ||
      host === 'billing.stripe.com' ||
      host === 'connect.stripe.com'
    );
  } catch {
    return false;
  }
}

/** Microsoft identity platform OAuth authorize URLs (admin-initiated CRM link). */
export function isMicrosoftOAuthRedirectUrl(raw: string | null | undefined): boolean {
  const s = sanitizeHttpUrlForHref(raw ?? '');
  if (!s) return false;
  try {
    const host = new URL(s).hostname.toLowerCase();
    return (
      host === 'login.microsoftonline.com' ||
      host === 'login.microsoft.com' ||
      host.endsWith('.microsoftonline.com')
    );
  } catch {
    return false;
  }
}

/**
 * Safe filename segment for `HTMLAnchorElement.download` (strips path/control chars).
 */
export function sanitizeDownloadFileName(raw: string | null | undefined, fallback = 'download'): string {
  if (raw == null || typeof raw !== 'string') return fallback;
  const s = raw
    .replace(/[/\\?*:|"<>]/g, '_')
    .replace(/[\x00-\x1f\x7f]/g, '')
    .trim()
    .slice(0, 120);
  return s || fallback;
}

/** Safe `href` for React/MUI link buttons — normalized tel: */
export function toTelHrefAttribute(raw: string | null | undefined): string | undefined {
  return sanitizeTelHref(raw) ?? undefined;
}

/** Safe `href` for mailto links. */
export function toMailtoHrefAttribute(raw: string | null | undefined): string | undefined {
  return sanitizeMailtoHref(raw) ?? undefined;
}

/** Safe `href` for http(s) external links. */
export function toHttpHrefAttribute(raw: string | null | undefined): string | undefined {
  return sanitizeHttpUrlForHref(raw) ?? undefined;
}

/**
 * Relative artifact path (e.g. test screenshots). Blocks path traversal and scheme tricks.
 */
export function sanitizeArtifactRelativePath(raw: string | null | undefined): string | null {
  if (raw == null || typeof raw !== 'string') return null;
  const t = raw.trim().replace(/^\/+/, '');
  if (!t || t.length > 600) return null;
  if (t.includes('..') || t.includes('\\') || t.startsWith('//')) return null;
  if (!/^[a-zA-Z0-9/_.-]+$/.test(t)) return null;
  return `/${t}`;
}

/** img src for test artifacts: absolute http(s) or strict relative path. */
export function sanitizeTestScreenshotImgSrc(
  screenshotUrl: string | null | undefined,
  fallback: string
): string {
  if (screenshotUrl == null || typeof screenshotUrl !== 'string') return fallback;
  const s = screenshotUrl.trim();
  if (!s) return fallback;
  if (/^https?:\/\//i.test(s)) {
    return sanitizeImgSrc(s, fallback);
  }
  const rel = sanitizeArtifactRelativePath(s);
  if (!rel) return fallback;
  return sanitizeImgSrc(rel, fallback);
}

